import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgxEditorModule, Editor, Toolbar } from 'ngx-editor';
import { schema as ngxSchema } from 'ngx-editor/schema';
import { Schema } from 'prosemirror-model';
import { tableNodes, tableEditing } from 'prosemirror-tables';
import { DocumentService } from '../../../services/document.service';
import * as mammoth from 'mammoth';

// Extended ProseMirror schema that includes table nodes
const extendedNodes = ngxSchema.spec.nodes.append(
  tableNodes({ tableGroup: 'block', cellContent: 'block+', cellAttributes: {} }) as any
);
const tableSchema = new Schema({ nodes: extendedNodes, marks: ngxSchema.spec.marks });

export interface VariableGroup {
  label: string;
  vars: { token: string; description: string }[];
}

const EMPLOYEE_VARIABLES: VariableGroup[] = [
  {
    label: 'Basic Information',
    vars: [
      { token: '{{ $employee_id }}',        description: 'Employee ID' },
      { token: '{{ $first_name }}',          description: 'First name' },
      { token: '{{ $last_name }}',           description: 'Last name' },
      { token: '{{ $email_address }}',       description: 'Primary email' },
      { token: '{{ $secondary_email }}',     description: 'Secondary email' },
      { token: '{{ $status }}',              description: 'Employment status' },
      { token: '{{ $gender }}',              description: 'Gender' },
      { token: '{{ $birthdate }}',           description: 'Date of birth' },
      { token: '{{ $marital_status }}',      description: 'Marital status' },
      { token: '{{ $address_primary }}',     description: 'Primary address' },
      { token: '{{ $phone_primary }}',       description: 'Primary phone' },
      { token: '{{ $address_secondary }}',   description: 'Secondary address' },
      { token: '{{ $phone_secondary }}',     description: 'Secondary phone' },
    ]
  },
  {
    label: 'Job Details',
    vars: [
      { token: '{{ $job_code }}',            description: 'Job code' },
      { token: '{{ $role }}',                description: 'Job role/title' },
      { token: '{{ $date_hired }}',          description: 'Date hired' },
      { token: '{{ $end_of_contract }}',     description: 'End of contract date' },
      { token: '{{ $agreed_hours }}',        description: 'Agreed working hours' },
      { token: '{{ $pto }}',                 description: 'PTO type' },
      { token: '{{ $hmo_eligibility_status }}', description: 'HMO eligibility' },
      { token: '{{ $client_relations }}',    description: 'Client relations' },
      { token: '{{ $recruiter_in_charge }}', description: 'Recruiter in charge' },
      { token: '{{ $dtr_required }}',        description: 'DTR required (true/false)' },
    ]
  },
  {
    label: 'Milestone Dates',
    vars: [
      { token: '{{ $first_month }}',  description: '1st month date' },
      { token: '{{ $eop_3rd_month }}', description: 'End of 3rd month' },
      { token: '{{ $six_months }}',   description: '6-month milestone' },
      { token: '{{ $nine_months }}',  description: '9-month milestone' },
      { token: '{{ $one_year }}',     description: '1-year milestone' },
      { token: '{{ $two_year }}',     description: '2-year milestone' },
      { token: '{{ $three_year }}',   description: '3-year milestone' },
    ]
  },
  {
    label: 'Bank Details',
    vars: [
      { token: '{{ $bank_name }}',           description: 'Bank name' },
      { token: '{{ $bank_code }}',           description: 'Bank code' },
      { token: '{{ $bank_account_holder }}', description: 'Account holder name' },
      { token: '{{ $bank_account_number }}', description: 'Account number' },
      { token: '{{ $bank_address }}',        description: 'Bank address' },
      { token: '{{ $swift_bic }}',           description: 'SWIFT/BIC code' },
    ]
  },
  {
    label: 'Emergency Contacts',
    vars: [
      { token: '{{ $primary_contact_name }}',     description: 'Primary contact name' },
      { token: '{{ $primary_contact_relation }}', description: 'Primary contact relation' },
      { token: '{{ $primary_contact_phone }}',    description: 'Primary contact phone' },
      { token: '{{ $secondary_contact_name }}',   description: 'Secondary contact name' },
      { token: '{{ $secondary_contact_relation }}', description: 'Secondary contact relation' },
      { token: '{{ $secondary_contact_phone }}',  description: 'Secondary contact phone' },
    ]
  },
  {
    label: 'Salary',
    vars: [
      { token: '{{ $salary_va_rate }}',         description: 'VA pay rate' },
      { token: '{{ $salary_va_frequency }}',    description: 'VA pay frequency' },
      { token: '{{ $salary_client_rate }}',     description: 'Client billing rate' },
      { token: '{{ $salary_client_frequency }}', description: 'Client billing frequency' },
      { token: '{{ $salary_currency }}',        description: 'Currency' },
      { token: '{{ $salary_effective_date }}',  description: 'Effective date' },
    ]
  },
  {
    label: 'Client (via relationship)',
    vars: [
      { token: '{{ $client_code }}',            description: 'Client code' },
      { token: '{{ $client_name }}',            description: 'Client company name' },
      { token: '{{ $client_website }}',         description: 'Client website' },
      { token: '{{ $client_direct_supervisor }}', description: 'Direct supervisor name' },
    ]
  },
  {
    label: 'Signature',
    vars: [
      { token: '{{ $signature }}',    description: 'Signee signature' },
      { token: '{{ $current_date }}', description: 'Current date when signed' },
    ]
  },
];

const CLIENT_VARIABLES: VariableGroup[] = [
  {
    label: 'Client Information',
    vars: [
      { token: '{{ $client_code }}',              description: 'Client code' },
      { token: '{{ $client_name }}',              description: 'Company/client name' },
      { token: '{{ $website }}',                  description: 'Website' },
      { token: '{{ $direct_supervisor_name }}',   description: 'Direct supervisor' },
      { token: '{{ $contact_first_name_primary }}', description: 'Primary contact first name' },
      { token: '{{ $contact_last_name_primary }}',  description: 'Primary contact last name' },
      { token: '{{ $contact_email_primary }}',    description: 'Primary contact email' },
      { token: '{{ $phone_primary }}',            description: 'Primary contact phone' },
      { token: '{{ $contact_first_name_secondary }}', description: 'Secondary contact first name' },
      { token: '{{ $contact_last_name_secondary }}',  description: 'Secondary contact last name' },
      { token: '{{ $contact_email_secondary }}',  description: 'Secondary contact email' },
      { token: '{{ $phone_secondary }}',          description: 'Secondary contact phone' },
    ]
  },
  {
    label: 'Signature',
    vars: [
      { token: '{{ $signature }}',    description: 'Signee signature' },
      { token: '{{ $current_date }}', description: 'Current date when signed' },
    ]
  },
];

@Component({
  selector: 'app-document-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxEditorModule, RouterModule],
  providers: [],
  templateUrl: './document-editor.component.html',
  styleUrls: ['./document-editor.component.scss']
})
export class DocumentEditorComponent implements OnInit, OnDestroy {
  safeContent: SafeHtml = '';

  constructor(
    private documentService: DocumentService,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {}

  isSaving = false;
  saveError = '';
  saveSuccess = false;

  // Sidebar resize
  sidebarWidth = 220;
  private _resizing = false;
  private _resizeStartX = 0;
  private _resizeStartW = 0;
  private _resizeMouseMove = (e: MouseEvent) => {
    if (!this._resizing) return;
    const delta = e.clientX - this._resizeStartX;
    const clamped = Math.min(420, Math.max(160, this._resizeStartW + delta));
    this.sidebarWidth = clamped;
  };
  private _resizeMouseUp = () => {
    this._resizing = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', this._resizeMouseMove);
    document.removeEventListener('mouseup', this._resizeMouseUp);
  };

  onResizeMouseDown(e: MouseEvent) {
    e.preventDefault();
    this._resizing = true;
    this._resizeStartX = e.clientX;
    this._resizeStartW = this.sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', this._resizeMouseMove);
    document.addEventListener('mouseup', this._resizeMouseUp);
  }

  toolbar: Toolbar = [
    [
      { heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }, // Headings (h1 = heading, h2 = subheading, h3+ = smaller)
      'bold', 'italic', 'underline', 'strike'
    ],
    ['ordered_list', 'bullet_list'],
    ['blockquote', 'code'],
    ['image'], // Only image button
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['undo', 'redo']
  ];

  uploadImage(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = e.target.result as string;
        // Focus editor then insert image using base64 data URL
        this.editor.commands.focus().exec();
        this.editor.commands.insertImage(base64).exec();
      };
      reader.readAsDataURL(file);
      input.value = '';
    }
  }

  pasteAsPlainText() {
    navigator.clipboard.readText()
      .then(text => {
        this.editor.commands.focus().exec();
        this.editor.commands.insertText(text).exec();
      })
      .catch(() => {
        alert('Unable to access clipboard. Please allow clipboard permissions or use Ctrl+V.');
      });
  }

  pasteWithFormatting() {
    if (!navigator.clipboard?.read) {
      alert('Your browser does not support reading formatted clipboard content. Use Ctrl+V instead.');
      return;
    }
    navigator.clipboard.read()
      .then(items => {
        for (const item of items) {
          if (item.types.includes('text/html')) {
            item.getType('text/html').then(blob => {
              blob.text().then(html => {
                this.editor.commands.focus().exec();
                this.editor.commands.insertHTML(this.cleanGoogleDocsHtml(html)).exec();
              });
            });
            return;
          }
          if (item.types.includes('text/plain')) {
            item.getType('text/plain').then(blob => {
              blob.text().then(text => {
                this.editor.commands.focus().exec();
                this.editor.commands.insertText(text).exec();
              });
            });
          }
        }
      })
      .catch(() => {
        alert('Unable to access clipboard. Please allow clipboard permissions or use Ctrl+V.');
      });
  }

  private cleanGoogleDocsHtml(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // Remove Google Docs wrapper spans that carry only style but no semantic meaning
    doc.querySelectorAll('span').forEach(span => {
      if (!span.querySelector('img') && span.style.cssText) {
        const fontWeight = span.style.fontWeight;
        const fontStyle  = span.style.fontStyle;
        const textDecoration = span.style.textDecoration;
        // Keep meaningful styles only
        span.removeAttribute('style');
        span.removeAttribute('class');
        if (fontWeight === 'bold' || fontWeight === '700') span.style.fontWeight = 'bold';
        if (fontStyle === 'italic') span.style.fontStyle = 'italic';
        if (textDecoration && textDecoration.includes('underline')) span.style.textDecoration = 'underline';
        if (textDecoration && textDecoration.includes('line-through')) span.style.textDecoration = 'line-through';
      }
    });
    // Strip inline styles from block elements (keep tag semantics)
    doc.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, ul, ol, table, tr, td, th').forEach(el => {
      el.removeAttribute('style');
      el.removeAttribute('class');
      el.removeAttribute('dir');
      el.removeAttribute('id');
    });
    // Remove empty spans
    doc.querySelectorAll('span:empty').forEach(el => el.remove());
    return doc.body.innerHTML;
  }

  docxUploading = false;
  showColumnsMenu = false;
  showPreview = false;

  openPreview() {
    this.safeContent = this.sanitizer.bypassSecurityTrustHtml(this.content || '');
    this.showPreview = true;
  }

  @HostListener('document:click')
  onDocClick() { this.showColumnsMenu = false; }

  insertColumns(count: 2 | 3) {
    this.showColumnsMenu = false;
    const { state } = this.editor.view;
    const s = state.schema;
    const cells = Array.from({ length: count }, () =>
      s.nodes['table_cell'].create(null, s.nodes['paragraph'].create())
    );
    const row = s.nodes['table_row'].create(null, cells);
    const table = s.nodes['table'].create(null, [row]);
    const tr = state.tr.replaceSelectionWith(table);
    this.editor.view.dispatch(tr);
  }

  uploadDocx(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;
    const file = input.files[0];
    input.value = '';
    this.docxUploading = true;
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      try {
        const arrayBuffer = e.target.result as ArrayBuffer;
        const result = await mammoth.convertToHtml({ arrayBuffer });
        const cleaned = this.cleanGoogleDocsHtml(result.value);
        this.content = cleaned;
      } catch {
        alert('Failed to read the .docx file. Make sure it is a valid Word document.');
      } finally {
        this.docxUploading = false;
      }
    };
    reader.onerror = () => {
      alert('Failed to read file.');
      this.docxUploading = false;
    };
    reader.readAsArrayBuffer(file);
  }
  type: 'client' | 'employee' = 'employee';
  department = '';
  title = '';
  content = '';
  requiresSignature = true;
  editor!: Editor;
  editId: number | null = null;
  isLoadingDoc = false;

  showVariables = false;
  varSearch = '';

  get variableGroups(): VariableGroup[] {
    const groups = this.type === 'client' ? CLIENT_VARIABLES : EMPLOYEE_VARIABLES;
    if (!this.varSearch.trim()) return groups;
    const q = this.varSearch.trim().toLowerCase();
    return groups
      .map(g => ({ ...g, vars: g.vars.filter(v => v.token.includes(q) || v.description.toLowerCase().includes(q)) }))
      .filter(g => g.vars.length > 0);
  }

  insertVariable(token: string) {
    const view = this.editor.view;
    view.focus();
    const { state } = view;
    const { from, to } = state.selection;
    view.dispatch(state.tr.insertText(token, from, to));
  }

  ngOnInit() {
    this.editor = new Editor({
      schema: tableSchema,
      plugins: [tableEditing()],
    });
    const params = this.route.snapshot.queryParamMap;
    const id = Number(params.get('id'));
    const t = params.get('type');

    if (id) {
      this.editId = id;
      this.isLoadingDoc = true;
      this.documentService.getOne(id).subscribe({
        next: (res: any) => {
          const doc = res.data ?? res;
          this.title      = doc.title      || '';
          this.department = doc.department || '';
          this.content    = doc.content    || '';
          this.requiresSignature = doc.requires_signature !== undefined ? !!doc.requires_signature : true;
          const docType   = doc.type;
          if (docType === 'client' || docType === 'employee') this.type = docType;
          this.isLoadingDoc = false;
        },
        error: () => {
          this.saveError = 'Failed to load document for editing.';
          this.isLoadingDoc = false;
        }
      });
    } else {
      if (t === 'client' || t === 'employee') this.type = t;
    }
  }

  ngOnDestroy() {
    this.editor?.destroy();
    document.removeEventListener('mousemove', this._resizeMouseMove);
    document.removeEventListener('mouseup', this._resizeMouseUp);
  }

  saveDocument() {
    if (!this.title) {
      alert('Please enter a document title.');
      return;
    }
    if (!this.department) {
      alert('Please enter a department.');
      return;
    }
    this.isSaving = true;
    this.saveError = '';
    this.saveSuccess = false;
    const payload = {
      title: this.title,
      type: this.type,
      department: this.department,
      content: this.content,
      requires_signature: this.requiresSignature
    };
    const request$ = this.editId
      ? this.documentService.update(this.editId, payload)
      : this.documentService.create(payload);
    request$.subscribe({
        next: () => {
          this.isSaving = false;
          const notice = this.editId ? 'updated' : 'created';
          this.router.navigate(['/documents'], { queryParams: { notice } });
        },
        error: (err) => {
          this.isSaving = false;
          this.saveError = err?.error?.message || 'Failed to save document. Please try again.';
        }
      });
  }
}
